import { z } from 'zod';
import type { TFunction } from 'i18next';

export const createLoginSchema = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.emailInvalid')),
    password: z
      .string()
      .min(1, t('validation.passwordRequired'))
      .min(6, t('validation.passwordMin6')),
  });

export type LoginFormData = {
  email: string;
  password: string;
};

export const createRegisterSchema = (t: TFunction) =>
  z
    .object({
      firstName: z
        .string()
        .min(1, t('validation.firstNameRequired'))
        .min(2, t('validation.firstNameMin'))
        .max(50, t('validation.firstNameMax')),
      lastName: z
        .string()
        .min(1, t('validation.lastNameRequired'))
        .min(2, t('validation.lastNameMin'))
        .max(50, t('validation.lastNameMax')),
      email: z
        .string()
        .min(1, t('validation.emailRequired'))
        .email(t('validation.emailInvalid')),
      password: z
        .string()
        .min(1, t('validation.passwordRequired'))
        .min(8, t('validation.passwordMin8'))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t('validation.passwordPattern')
        ),
      confirmPassword: z.string().min(1, t('validation.confirmPasswordRequired')),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t('validation.acceptTermsRequired'),
      }),
      acceptHealthDataConsent: z.boolean().refine((val) => val === true, {
        message: t('validation.acceptHealthDataRequired'),
      }),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });

export type RegisterFormData = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  acceptTerms: boolean;
  acceptHealthDataConsent: boolean;
};

export const createResetPasswordSchema = (t: TFunction) =>
  z.object({
    email: z
      .string()
      .min(1, t('validation.emailRequired'))
      .email(t('validation.emailInvalid')),
  });

export type ResetPasswordFormData = {
  email: string;
};

export const createNewPasswordSchema = (t: TFunction) =>
  z
    .object({
      password: z
        .string()
        .min(1, t('validation.passwordRequired'))
        .min(8, t('validation.passwordMin8'))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t('validation.passwordPattern')
        ),
      confirmPassword: z.string().min(1, t('validation.confirmPasswordRequired')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('validation.passwordsMismatch'),
      path: ['confirmPassword'],
    });

export type NewPasswordFormData = {
  password: string;
  confirmPassword: string;
};
